import type { ReactNode } from 'react'
import { Panel } from './primitives/Panel'
import { Fact, FactList, Mono } from './primitives/Fact'
import { EnvironmentStatusChip } from './primitives/StatusChip'
import { principalClassLabel } from '../theme/vocabulary'
import { expiryState } from '../api/expiry'
import { expiryTone, toneVisual } from '../theme/statusTone'
import type { EnvironmentCredential } from '../api/types'

// One panel per credential (ATC-131 / ATC-123).
//
// `Get-EnvironmentDetail` took `$rows | Select-Object -First 1` over a
// partition that may hold BOTH a device and a flightdeck row, and never
// surfaced `principalClass` — so the page reported one arbitrarily and never
// said which. On such an environment "rotate the token" and "edit the
// entitlements" are ambiguous questions, and the API's selector has been
// answerable-but-unasked since `ATC-ADR-016`.

/** What an operator actually recognises in a config blob. */
function tokenPrefix(principalClass: string): string {
  return principalClass === 'flightdeck' ? 'atc_fd_' : 'atc_lt_'
}

export function CredentialPanel({
  credential,
  now,
  actions,
}: {
  credential: EnvironmentCredential
  /** Injected so the expiry chip is deterministic in tests. */
  now: number
  actions?: ReactNode
}) {
  const state = expiryState(credential.expiresAt, now)
  const tone = expiryTone(state)

  return (
    <Panel title={principalClassLabel(credential.principalClass)} action={actions}>
      <FactList>
        <Fact label="Status">
          {/* EXPIRY IS A SECOND CHIP BESIDE STATUS, never a fourth status value
              and never a sixth tone. An environment can be `active` AND
              expired: Get-Entitlements refuses it with `token_expired` while
              every list endpoint still reports a green `active`. Folding them
              would produce exactly the benign-looking terminal state
              ATC-ADR-018 §1.3 exists to prevent. */}
          <EnvironmentStatusChip status={credential.status} />
          {tone && (
            <span
              className="rx-chip"
              style={
                {
                  '--rx-chip-tint': toneVisual(tone).tint,
                  '--rx-chip-dot': toneVisual(tone).hex,
                } as React.CSSProperties
              }
            >
              <span className="rx-chip-dot" aria-hidden="true" />
              {state === 'expired' ? 'Expired' : 'Expiring'}
            </span>
          )}
        </Fact>
        <Fact label="Token prefix">
          <Mono>{tokenPrefix(credential.principalClass)}…</Mono>
        </Fact>
        <Fact label="Entitlements">
          {credential.entitledServices.length > 0 ? (
            credential.entitledServices.join(', ')
          ) : (
            <span className="muted">None</span>
          )}
        </Fact>
        <Fact label="Issued">
          <Mono>{credential.issuedAt}</Mono>
        </Fact>
        <Fact label="Expires">
          {credential.expiresAt ? <Mono>{credential.expiresAt}</Mono> : 'never'}
        </Fact>
      </FactList>
    </Panel>
  )
}
